import { Chip, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useState } from 'react';

import { canonicalValue } from '../conditionValues';
import { CatalogFieldOption } from '../types';

export interface EntriesFieldProps {
  /** Lands on the input, so a label can point at it. */
  id: string;
  entries: string[];
  options: CatalogFieldOption[];
  /** `small` in the editor rail; the Send Offer form uses MUI's default. */
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  /**
   * The ids of the text that describes the input, such as a rule line. Name
   * the helper text's id too: this replaces the one MUI sets.
   */
  describedBy?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  onEntriesChange: (entries: string[]) => void;
}

const labelFor = (options: CatalogFieldOption[], value: string): string =>
  options.find(option => option.value === value)?.label ?? value;

/**
 * Several entries over a key's options. Each entry is a chip named by its
 * option's label; the options are listed when the key has any, and a typed
 * entry is added on Enter or when the field loses focus. Entries never pass
 * through a separator, so `Dallas, TX` stays one entry.
 */
export const EntriesField: React.FC<EntriesFieldProps> = ({
  id,
  entries,
  options,
  size = 'small',
  fullWidth = false,
  describedBy,
  error,
  helperText,
  disabled,
  onEntriesChange,
}) => {
  const [draft, setDraft] = useState('');

  const commit = (next: string[]) => {
    const canonical = next
      .map(entry => canonicalValue(options, entry).trim())
      .filter(entry => entry !== '');
    onEntriesChange(Array.from(new Set(canonical)));
  };

  return (
    <Autocomplete<string>
      id={id}
      multiple
      freeSolo
      forcePopupIcon={options.length > 0}
      filterSelectedOptions
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      options={options.map(option => option.value)}
      value={entries}
      inputValue={draft}
      getOptionLabel={entry => labelFor(options, entry)}
      onInputChange={(_event, text, reason) => {
        if (reason === 'input') setDraft(text);
      }}
      onChange={(_event, next) => {
        setDraft('');
        commit(next);
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((entry, index) => (
          <Chip
            size="small"
            label={labelFor(options, entry)}
            // A long entry truncates inside the field; hover shows it whole.
            title={labelFor(options, entry)}
            {...getTagProps({ index })}
            key={entry}
          />
        ))
      }
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          error={error}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            // Set only when given: these props land on the <input> after the
            // helper text id MUI puts there, so an undefined would erase it.
            ...(describedBy ? { 'aria-describedby': describedBy } : {}),
            onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
              // A typed entry not committed with Enter still counts.
              if (draft.trim() !== '') {
                commit([...entries, draft]);
                setDraft('');
              }
              (
                params.inputProps as React.InputHTMLAttributes<HTMLInputElement>
              ).onBlur?.(event);
            },
          }}
        />
      )}
    />
  );
};
